/** @format */

import {getDateArray} from 'helpers/dateHelpers';

import {GraphComplexValuesList, GraphStat} from '../../models/common';
import {GraphStatFromQuery, MediaplanStatFromQuery} from '../../models/api';
import {collectGraphStats} from './collectGraphStats';
import {collectMediaplanStats} from './collectMediaplanStats';
import {mergeCollections} from './mergeCollections';
import {collectGraphComplexValues} from './collectGraphComplexValues';
import {aggregateGraphStats} from './aggregateGraphStats';
import {aggregateGraphComplexValues} from './aggregateGraphComplexValues';
import {DTO_DATE_FORMAT} from '../../constants/common';
import {filterGraphComplexValues} from './filterGraphComplexValues';

export interface ReturnMappedDto {
  graphStats: GraphStat[];
  graphComplexValues: GraphComplexValuesList;
}

/**
 * Вернёт данные в формате, удобном для mapChartsData
 * Также вычислит все производные показатели по формулам. Подробнее:
 * https://docs.google.com/spreadsheets/d/1WKXrwXv_8y-EyQogiSOyltuAfEKY9AW1t3HwGoYJAIU/edit?gid=0#gid=0
 */

interface Options {
  graphStats: ReadonlyArray<GraphStatFromQuery>;
  mediaplanStats: ReadonlyArray<MediaplanStatFromQuery>;
  rechartsDates: string[];
  startDate: Date;
  endDate: Date;
}

const mapGraphStats = ({
  graphStats,
  mediaplanStats,
  rechartsDates,
  startDate,
  endDate,
}: Options): ReturnMappedDto => {
  const allDates = getDateArray({
    startDate,
    endDate,
    format: DTO_DATE_FORMAT,
  });

  // Собираем данные из graphStats
  const collectedGraphStats = collectGraphStats(graphStats);

  // Собираем данные из mediaplanStats
  const collectedMediaplanStats = collectMediaplanStats(mediaplanStats);

  // Склеиваем данные
  const mappedGraphStats = mergeCollections(
    [collectedGraphStats, collectedMediaplanStats],
    allDates,
  );

  // Сохраняем бэкап сырых данных (в будущем потребуется для статистики виджетов и прогнозов)
  const graphComplexValues = collectGraphComplexValues({
    graphStatsWithMediaplanStats: mappedGraphStats,
    allDates,
  });

  // Агрегируем данные в соответствии с настройками дискретности
  const aggregatedGraphComplexValues = aggregateGraphComplexValues(
    graphComplexValues,
    rechartsDates,
  );
  const aggregatedGraphStats = aggregateGraphStats({
    graphStats: mappedGraphStats,
    complexList: aggregatedGraphComplexValues,
    rechartsDates,
  });

  // Удаляем ненужные даты, если выбрана дискретность больше дня
  const filteredGraphComplexValues = filterGraphComplexValues(
    aggregatedGraphComplexValues,
    rechartsDates,
  );

  return {
    graphStats: aggregatedGraphStats,
    graphComplexValues: filteredGraphComplexValues,
  };
};

export {mapGraphStats};
